import React from 'react'
import { Paper, TableCell, TableHead as MuiTableHead, TableRow, TableSortLabel, PaperProps, SxProps } from '@mui/material'
import { Theme } from '@mui/material/styles'
import type { CurrentSort, TableHeadProps } from '../types'
import useStyles from '../styles'

const PaperTableHead: React.FC<PaperProps<'thead'>> = props => <Paper component="thead" elevation={props.elevation ?? 1} {...props} />

const TableHead = <T extends { [key: string]: unknown }>({
  headerItems,
  className,
  customFunctionalities,
  currentSort,
  setCurrentSort,
  elevation,
  collapsibleContent,
  stickyHeader,
  stickyFirstColumn,
  stickyLastColumn,
}: TableHeadProps<T>): React.ReactElement => {
  const { classes } = useStyles({})

  return (
    <MuiTableHead component={PaperTableHead} classes={{ root: className }} elevation={elevation}>
      <TableRow className={classes.headerRow}>
        {collapsibleContent && <TableCell key={'collapse-icon-head'} />}
        {headerItems.map((item, columnIndex) => {
          const isFirstColumn = columnIndex === 0
          const isLastColumn = columnIndex === headerItems.length - 1

          const shouldFirstColumnBeSticky = isFirstColumn && stickyFirstColumn
          const shouldLastColumnBeSticky = isLastColumn && stickyLastColumn

          const stickyHeaderSx: SxProps<Theme> = {
            position: stickyHeader ? 'sticky' : 'static',
            top: 0,
            zIndex: 1 /** make sure sticky header is one level above the normal cells which sit at 0(implied by "auto") */,
          }

          const firstColumnStickySx: SxProps<Theme> = {
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 2,
            borderRight: theme => `1px solid ${theme.palette.grey.grey06}`,
          }

          const lastColumnStickySx: SxProps<Theme> = {
            position: 'sticky',
            top: 0,
            right: 0,
            zIndex: 2,
            borderLeft: theme => `1px solid ${theme.palette.grey.grey06}`,
          }

          return (
            <TableCell
              key={columnIndex}
              onClick={
                item.label && currentSort && item.isSortable
                  ? () => {
                      if (item.value === currentSort?.name)
                        setCurrentSort(state => ({ ...state, direction: state?.direction === 'asc' ? 'desc' : 'asc' } as CurrentSort<T>))
                      else setCurrentSort({ name: item.value, direction: 'asc' })
                    }
                  : undefined
              }
              align={item.align ?? 'left'}
              width={item.width}
              sx={{
                minWidth: item.width,
                backgroundColor: theme => theme.palette.background.paper,
                ...(stickyHeader ? stickyHeaderSx : {}),
                ...(shouldFirstColumnBeSticky ? firstColumnStickySx : {}),
                ...(shouldLastColumnBeSticky ? lastColumnStickySx : {}),
              }}
            >
              {item.label && currentSort && item.isSortable ? (
                <TableSortLabel active={item.value === currentSort?.name} direction={currentSort?.direction}>
                  {item.label}
                </TableSortLabel>
              ) : (
                item.label
              )}
            </TableCell>
          )
        })}
        {customFunctionalities?.map((item, index) => (
          <TableCell key={index} />
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

export default TableHead
